import { Button, Input } from "antd";
import { Element, scroller } from "react-scroll";
import React, { FC, useCallback, useContext, useEffect, useState } from "react";
import TaskPanel from "Components/TaskPanel";
import { StatusType, TaskType } from "App.types";
import { SentenceTaskProps } from "../SentenceTask.type";
import styles from "./ChatTask.module.scss";
import API from "Api";
import { useSuspenseQuery } from "@tanstack/react-query";
import ChatMessage from "./ChatMessage";
import { SendOutlined } from "@ant-design/icons";
import { NotifyContext } from "App";
import { isMobile } from "react-device-detect";

type ChatMessageType = { role: string; text: string; translate?: string; isCompleted?: boolean };

const ChatTask: FC<SentenceTaskProps> = ({ lesson, sentence, onTaskComplete, onNext, children }) => {
  const [status, setStatus] = useState<StatusType>();
  const [text, setText] = useState<string>("");
  const [comments, setComments] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [loading, setLoading] = useState(false);

  const { linkedStoryId: storyId = 0 } = sentence;

  const notifyApi = useContext(NotifyContext);

  const { data: history, refetch } = useSuspenseQuery({
    queryKey: ["messages", storyId],
    queryFn: () => API.chat.messages(storyId),
  });

  useEffect(() => {
    const [, ...lastMessages] = history;
    setMessages(lastMessages);
  }, [history]);

  const sendMessage = useCallback(async () => {
    if (!status || text.trim()) {
      if (text.trim()) {
        setText("");
        setComments("");

        if (status === StatusType.Error) {
          setMessages((prev) => {
            prev.pop();
            return [...prev, { role: "user", text }];
          });
        } else {
          setMessages((prev) => [...prev, { role: "user", text }]);
        }
      }

      setLoading(true);
      const response = await API.chat.send({ storyId, message: text.replace(/^\n/, "") || "." }).finally(() => setLoading(false));

      if (response.comments) {
        if (response.isWrong) {
          notifyApi.error({ message: <div dangerouslySetInnerHTML={{ __html: response.comments }} />, duration: 10 });
        } else {
          notifyApi.info({ message: <div dangerouslySetInnerHTML={{ __html: response.comments }} />, duration: 10 });
        }
      }

      if (response.isWrong) {
        setText(text);
        setStatus(StatusType.Error);
        setComments(response.comments);
        return;
      }

      setStatus(StatusType.Empty);

      if (response.text) {
        setMessages((prev) => [...prev, { role: "assistant", ...response }]);
      }
      if (response.isCompleted) {
        setStatus(StatusType.Completed);
        onTaskComplete();
      }
    }
  }, [status, text, storyId, notifyApi, onTaskComplete]);

  const onStart = useCallback(() => {
    setMessages([]);

    if (history.some((m) => m.isCompleted)) {
      API.chat.delete(storyId).then(() => {
        refetch().then(() => sendMessage().then(() => setStatus(StatusType.Empty)));
      });
    } else {
      sendMessage().then(() => setStatus(StatusType.Empty));
    }
  }, [history, refetch, sendMessage, storyId]);

  useEffect(() => {
    if (messages.length) {
      scroller.scrollTo(`${messages.length - 1}`, { smooth: true, isDynamic: true, offset: -100 });
    }
  }, [messages.length]);

  return (
    <div className={styles.chatTask}>
      <div className={styles.messages}>
        {!status && !isMobile ? (
          <div className={styles.children}>{children}</div>
        ) : (
          messages.map((message, idx) => (
            <Element name={`${idx}`} key={idx}>
              <ChatMessage
                info={idx === messages.length - 1 && status === StatusType.Error ? comments : ""}
                active={idx === messages.length - 1 && !!status}
                message={message}
                onCompleted={() => {}}
              />
            </Element>
          ))
        )}
        {loading && (
          <div className={styles.typing}>
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      <TaskPanel
        onNext={onNext}
        isCompleted={status === StatusType.Completed}
        task={TaskType.Chat}
        lessonId={lesson.id}
        sentId={sentence.id}
        storyId={sentence.linkedStoryId}
      >
        {status ? (
          <>
            <Input.TextArea onPressEnter={sendMessage} className={styles.input} value={text} onChange={(e) => setText(e.target.value)} />
            <Button icon={<SendOutlined />} type={"link"} onClick={sendMessage} />
          </>
        ) : (
          <Button loading={loading} variant={"outlined"} danger shape={"round"} onClick={onStart}>
            start
          </Button>
        )}
      </TaskPanel>
      <div className={styles.placeholder} />
    </div>
  );
};

export default ChatTask;
